<template>
  <v-alert
    :color="color"
    :icon="computedIcon"
    text
    :outlined="outlined"
    :border="computedBorder"
    :class="computedClass"
  >
    <template v-slot:prepend>
      <v-icon
        :color="color"
        :class="{ 'alert-icon-top': collapsible || iconTop }"
        :size="iconSize"
      >
        {{ computedIcon }}
      </v-icon>
    </template>
    <!-- collapsible: always-visible summary row acts as the toggle -->
    <div
      v-if="collapsible"
      class="alert-summary"
      role="button"
      tabindex="0"
      :aria-expanded="String(expanded)"
      :style="{ fontSize: fontSize }"
      @click="expanded = !expanded"
      @keydown.enter.space.prevent="expanded = !expanded"
    >
          <span>
        <strong v-if="summaryLabel">{{ summaryLabel }}</strong>
        {{ summary }}
      </span>
      <v-icon
        :color="color"
        size="18"
        class="alert-toggle"
        :class="{ 'is-expanded': expanded }"
      >
        {{ icons.mdiChevronDown }}
      </v-icon>
    </div>

    <v-expand-transition v-if="collapsible">
      <div
        v-show="expanded"
        class="alert-text alert-body"
        :style="{ fontSize: bodyFontSize }"
      >
        <slot></slot>
      </div>
    </v-expand-transition>

    <div
      v-else
      class="alert-text"
      :style="{ fontSize: fontSize, color: textColor }"
    >
      <slot></slot>
    </div>
  </v-alert>
</template>

<script>
import {
  mdiAlert,
  mdiAlertCircle,
  mdiAlertOctagon,
  mdiChevronDown,
  mdiInformation,
  mdiThumbUp,
} from '@mdi/js'

export default {
  props: {
    color: {
      type: String,
      default: 'warning',
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    border: {
      type: String,
      default: 'left',
    },
    fontSize: {
      type: String,
      default: '16px',
    },
    iconShape: {
      type: String,
      default: '',
    },
    textColor: {
      type: String,
      default: 'warning',
    },
    roundedBorder: {
      type: Boolean,
      default: false,
    },
    iconSize: {
      type: Number,
      default: 20,
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
    summary: {
      type: String,
      default: '',
    },
    summaryLabel: {
      type: String,
      default: '',
    },
    bodyFontSize: {
      type: String,
      default: '13px',
    },
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
    iconTop: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: this.defaultExpanded,
      icons: {
        mdiAlert, mdiAlertOctagon, mdiThumbUp, mdiInformation, mdiAlertCircle, mdiChevronDown,
      },
    }
  },
  computed: {
    computedIcon() {
      // Can be extended later on for different shapes by having a method getIconBasedOnShape
      // That returns the desired shape for the icon based on the iconShape prop.
      switch (this.color) {
        case 'error': return this.icons.mdiAlertOctagon
        case 'warning': {
          if (this.iconShape === 'circle') return this.icons.mdiAlertCircle

          return this.icons.mdiAlert
        }
        case 'primary': return this.icons.mdiInformation
        case 'success': return this.icons.mdiThumbUp
        default: return this.icons.mdiInformation
      }
    },
    computedClass() {
      const classes = []

      if (this.roundedBorder) classes.push('rounded-border')

      return classes
    },
    computedBorder() {
      return ['left', 'right', 'top', 'bottom'].includes(this.border) ? this.border : undefined
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep .v-alert__content {
  margin-left: 10px;
}
.rounded-border {
  border-radius: 10px;
}
.alert-text {
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
}
.alert-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}
.alert-toggle {
  transition: transform 0.2s ease-out;
  &.is-expanded {
    transform: rotate(180deg);
  }
}
.alert-body {
  margin-top: 8px;
  color: var(--v-secondary-darken2);
}
.alert-icon-top {
  align-self: flex-start;
  margin-top: 1px;
}
</style>
